import dayjs from "dayjs";
import {
  CONTRACT_STATUS_ACTIVE,
  CONTRACT_STATUS_CANCELLED,
  CONTRACT_STATUS_DRAFT,
  CONTRACT_STATUS_ENDED,
  CONTRACT_STATUS_PAUSED,
} from "../../app/constants/contractStatus";
import { ISO_DATE_LENGTH } from "../../app/utils/isoDate";

const RANK_ACTIVE_FINISHED = 2;
const RANK_OTHER = 3;

export type RestPeriodContractChoice = {
  id: string;
  status?: number | string | null;
  startDate?: string | null;
  restPeriodTemplateId?: string | null;
  primaryContractEndDate?: string | null;
  effectiveMembershipEndDate?: string | null;
  cancellationEffectiveDate?: string | null;
  endDate?: string | null;
};

export function restPeriodContractEndDate(contract: RestPeriodContractChoice): string {
  const raw = contract.effectiveMembershipEndDate
    || contract.cancellationEffectiveDate
    || contract.primaryContractEndDate
    || contract.endDate
    || "";
  return String(raw).slice(0, ISO_DATE_LENGTH);
}

/** Letzter zulässiger Ruhezeit-*Beginn* bei beendetem Paket: min(heute, Vertragsende). Das Ende darf darüber hinausragen. */
export function latestAllowedRestPeriodDate(
  contract: RestPeriodContractChoice,
  today = dayjs().format("YYYY-MM-DD"),
): string | null {
  if (!isFinishedRestPeriodContract(contract, today)) return null;
  const end = restPeriodContractEndDate(contract);
  if (end && end < today) return end;
  return today;
}

export function isFinishedRestPeriodContract(
  contract: RestPeriodContractChoice,
  today = dayjs().format("YYYY-MM-DD"),
): boolean {
  const status = Number(contract.status);
  if (status === CONTRACT_STATUS_ENDED || status === CONTRACT_STATUS_CANCELLED) return true;
  const end = restPeriodContractEndDate(contract);
  const start = String(contract.startDate || "").slice(0, ISO_DATE_LENGTH);
  if (end && (end <= today || (start.length > 0 && end === start)) && status !== CONTRACT_STATUS_PAUSED) {
    return true;
  }
  return false;
}

function runningRank(contract: RestPeriodContractChoice): number {
  const status = Number(contract.status);
  if (status === CONTRACT_STATUS_ACTIVE && !isFinishedRestPeriodContract(contract)) return 0;
  if (status === CONTRACT_STATUS_PAUSED) return 1;
  if (status === CONTRACT_STATUS_ACTIVE) return RANK_ACTIVE_FINISHED;
  return RANK_OTHER;
}

export function sortRestPeriodContracts<T extends RestPeriodContractChoice>(packages: T[]): T[] {
  return [...packages].sort((left, right) => {
    const leftFinished = isFinishedRestPeriodContract(left) ? 1 : 0;
    const rightFinished = isFinishedRestPeriodContract(right) ? 1 : 0;
    if (leftFinished !== rightFinished) return leftFinished - rightFinished;
    const leftRank = runningRank(left);
    const rightRank = runningRank(right);
    if (leftRank !== rightRank) return leftRank - rightRank;
    const leftHasTemplate = left.restPeriodTemplateId != null ? 1 : 0;
    const rightHasTemplate = right.restPeriodTemplateId != null ? 1 : 0;
    if (leftHasTemplate !== rightHasTemplate) return rightHasTemplate - leftHasTemplate;
    return String(right.startDate || "").slice(0, ISO_DATE_LENGTH).localeCompare(String(left.startDate || "").slice(0, ISO_DATE_LENGTH));
  });
}

export function preferredRestPeriodContractId(
  packages: RestPeriodContractChoice[],
  prefillId?: string | null,
): string | null {
  const withoutDraft = packages.filter((contract) => Number(contract.status) !== CONTRACT_STATUS_DRAFT);
  const running = withoutDraft.filter((contract) => !isFinishedRestPeriodContract(contract));
  const pool = running.length > 0 ? running : withoutDraft;
  const sorted = sortRestPeriodContracts(pool);
  if (prefillId) {
    const prefill = withoutDraft.find((contract) => contract.id === prefillId);
    if (prefill) return prefill.id;
  }
  return sorted[0]?.id ?? null;
}
